/**
 * Harness authentication for the offline end-to-end suite.
 *
 * The E2E harness (frontend/tests/e2e/harness) starts the real backend with its own
 * `AUTH_PROVIDER=fake` port — the facility the backend already ships for tests and forbids in
 * production — and exposes one extra route that hands out one of those pre-registered tokens for
 * a seeded synthetic user. This adapter is the client half of that arrangement.
 *
 * It is refused in a production build by `createAuthAdapter`, so it cannot become a bypass:
 * there is no code path that selects it when `import.meta.env.PROD` is true, and the endpoint it
 * needs does not exist on the real backend at all.
 */
import { type AuthAdapter, type AuthState, SIGNED_OUT } from './adapter';
import { AuthFailure } from './supabaseAdapter';

const STORAGE_KEY = 'hpw-credentials-harness-session';

interface HarnessSession {
  token: string;
  email: string;
}

export class LocalHarnessAuthAdapter implements AuthAdapter {
  readonly name = 'harness_auth';
  private session: HarnessSession | null = null;
  private readonly listeners = new Set<(state: AuthState) => void>();

  constructor(private readonly endpoint: string) {}

  private read(): HarnessSession | null {
    if (this.session) return this.session;
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      this.session = raw ? (JSON.parse(raw) as HarnessSession) : null;
    } catch {
      this.session = null;
    }
    return this.session;
  }

  private write(session: HarnessSession | null): AuthState {
    this.session = session;
    try {
      if (session) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
      else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable: the session simply does not survive a reload */
    }
    const state = this.stateOf(session);
    for (const listener of this.listeners) listener(state);
    return state;
  }

  private stateOf(session: HarnessSession | null): AuthState {
    if (!session) return SIGNED_OUT;
    // The harness backend hands out ordinary sessions. Since PO 7.10 that is all an admin session
    // is, so there is no second step to simulate here and never was one to skip.
    return { status: 'ready', email: session.email, aal: 'aal1', hasToken: true };
  }

  async restore(): Promise<AuthState> {
    return this.stateOf(this.read());
  }

  async signInWithPassword(email: string, password: string): Promise<AuthState> {
    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) throw new AuthFailure('invalid_credentials');
    const body = (await response.json()) as { token?: string; email?: string };
    if (!body.token) throw new AuthFailure('auth_failed');
    return this.write({ token: body.token, email: body.email ?? email });
  }

  async signOut(): Promise<void> {
    this.write(null);
  }

  async getAccessToken(): Promise<string | null> {
    return this.read()?.token ?? null;
  }

  onChange(listener: (state: AuthState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /*
   * No recovery. The harness backend hands out pre-registered tokens; it has no password to reset
   * and no mail to send one about, so there is nothing here to simulate. Refusing is what the
   * screen needs — it shows the same safe sentence it shows for an unusable link — and simulating
   * one would put a working password form in front of a browser test that proved nothing about the
   * real provider.
   */
  readonly supportsRecovery = false;

  /*
   * Asking for a mail is refused for the same reason completing a link is: there is no account here
   * to send one about. Answering as though one had gone out would put an enumeration-safe
   * confirmation in front of a browser test that proved nothing — the harness has no mailbox.
   */
  async requestPasswordRecovery(): Promise<never> {
    throw new AuthFailure('recovery_not_supported');
  }

  async beginPasswordRecovery(): Promise<never> {
    throw new AuthFailure('recovery_not_supported');
  }

  async completePasswordRecovery(): Promise<never> {
    throw new AuthFailure('recovery_not_supported');
  }

  async endPasswordRecovery(): Promise<void> {
    /* nothing was ever established */
  }
}
